import React, { useState, useEffect } from 'react';
import { X, Loader, CreditCard, LogIn, UserPlus } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';
import { isSignupFirstFlow } from '../config/authFlow';
import { useAuth } from '../contexts/AuthContext';
import { PixPaymentModal } from './PixPaymentModal';
import toast from 'react-hot-toast';

interface AuthFlowModalProps {
  visible: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
  initialMode?: 'login' | 'signup';
}

export function AuthFlowModal({ visible, onClose, onLoginSuccess, initialMode = 'signup' }: AuthFlowModalProps) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingPaymentId = (location.state as any)?.pendingPaymentId;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [showPixModal, setShowPixModal] = useState(false);
  const [selectedPlanId] = useState('free');

  useEffect(() => {
    if (visible) {
      setIsLogin(initialMode === 'login');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    }
  }, [visible, initialMode]);

  const handleSubmit = async () => {
    if (!email.trim() || !password.trim()) {
      toast.error('Preencha email e senha', { style: { zIndex: 10001 } });
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      toast.error('As senhas não conferem', { style: { zIndex: 10001 } });
      return;
    }

    setIsLoading(true);

    try {
      const apiUrl = isLogin
        ? `${process.env.REACT_APP_API_URL}/sessions`
        : `${process.env.REACT_APP_API_URL}/accounts`;
      const method = 'POST';
      const requestBody = JSON.stringify({ email: email.trim(), password: password.trim() });

      const response = await fetch(apiUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: requestBody,
      });

      const responseData = await response.json();

      if (response.ok) {
        if (responseData.token) {
          localStorage.setItem('accessToken', responseData.token);
          localStorage.setItem('refreshToken', responseData.refreshToken || responseData.token);
          localStorage.setItem('user', JSON.stringify(responseData));
          login(responseData.token, responseData.refreshToken || responseData.token, responseData);

          // Se há um pagamento pendente (payment_first flow), ativar assinatura
          if (!isLogin && pendingPaymentId) {
            try {
              const activateResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/payment/activate-subscription/${pendingPaymentId}`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${responseData.token}`,
                  },
                  body: JSON.stringify({ userId: responseData.id }),
                }
              );

              if (activateResponse.ok) {
                toast.success('Conta criada e assinatura ativada!');
                onClose();
                // Redirecionar para home após ativar assinatura
                setTimeout(() => navigate('/'), 500);
              } else {
                toast.error('Conta criada, mas houve erro ao ativar assinatura');
                onClose();
                onLoginSuccess?.();
              }
            } catch {
              toast.error('Conta criada, mas houve erro ao ativar assinatura');
              onClose();
              onLoginSuccess?.();
            }
          } else {
            toast.success(isLogin ? 'Login realizado!' : 'Cadastro realizado!');
            onClose();
            onLoginSuccess?.();
          }
        }
      } else {
        // Extrair mensagem de erro da resposta
        const errorMessage =
          responseData?.errors?.[0]?.message ||
          responseData?.message ||
          (isLogin ? 'Email ou senha inválidos' : 'Erro ao cadastrar');
        toast.error(errorMessage);
      }
    } catch {
      toast.error('Erro ao conectar');
    } finally {
      setIsLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {isSignupFirstFlow()
              ? 'Criar Conta'
              : isLogin
                ? 'Entrar'
                : 'Criar Conta'}
          </h2>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={24} color={NETFLIX_COLORS.textSecondary} />
          </button>
        </div>

        <div style={contentStyle}>
          {isSignupFirstFlow() ? (
            <>
              <div style={fieldStyle}>
                <label style={labelStyle}>Email</label>
                <input
                  style={inputStyle}
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Senha</label>
                <input
                  style={inputStyle}
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              {!isLogin && (
                <div style={fieldStyle}>
                  <label style={labelStyle}>Confirmar Senha</label>
                  <input
                    style={inputStyle}
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              )}

              <button
                onClick={handleSubmit}
                style={isLoading ? buttonDisabledStyle : buttonStyle}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span style={loaderContainerStyle}>
                    <Loader size={16} style={loaderAnimStyle} />
                    Carregando...
                  </span>
                ) : isLogin ? (
                  <>
                    <LogIn size={16} style={iconMarginStyle} />
                    Entrar
                  </>
                ) : (
                  <>
                    <UserPlus size={16} style={iconMarginStyle} />
                    Cadastrar
                  </>
                )}
              </button>

              <button style={toggleButtonStyle} onClick={() => setIsLogin(!isLogin)}>
                {isLogin ? (
                  <>
                    <UserPlus size={14} style={toggleIconMarginStyle} />
                    Criar nova conta
                  </>
                ) : (
                  <>
                    <LogIn size={14} style={toggleIconMarginStyle} />
                    Já tenho conta
                  </>
                )}
              </button>
            </>
          ) : isLogin ? (
            <>
              <div style={fieldStyle}>
                <label style={labelStyle}>Email</label>
                <input
                  style={inputStyle}
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Senha</label>
                <input
                  style={inputStyle}
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div style={buttonGroupStyle}>
                <button
                  onClick={handleSubmit}
                  style={isLoading ? buttonDisabledStyle : mainButtonStyle}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span style={loaderContainerStyle}>
                      <Loader size={16} style={loaderAnimStyle} />
                      Carregando...
                    </span>
                  ) : (
                    <>
                      <LogIn size={16} style={iconMarginStyle} />
                      Fazer Login
                    </>
                  )}
                </button>
                <button
                  onClick={() => setIsLogin(false)}
                  style={alternativeButtonInactiveStyle}
                >
                  <UserPlus size={16} style={altIconMarginStyle} />
                  Cadastro
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={paymentInfoStyle}>
                <h3 style={paymentTitleStyle}>Comece Agora</h3>
                <p style={paymentDescriptionStyle}>
                  Pague com PIX e tenha acesso instantâneo a todo o catálogo.
                </p>
                <button
                  onClick={() => setShowPixModal(true)}
                  style={pixButtonStyle}
                >
                  <CreditCard size={20} color={NETFLIX_COLORS.white} />
                  <span>Pagar com PIX</span>
                </button>
              </div>

              <div style={dividerStyle}>
                <div style={dividerLine} />
                <span style={dividerTextStyle}>OU</span>
                <div style={dividerLine} />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Email</label>
                <input
                  style={inputStyle}
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Senha</label>
                <input
                  style={inputStyle}
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Confirmar Senha</label>
                <input
                  style={inputStyle}
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div style={buttonGroupStyle}>
                <button
                  onClick={handleSubmit}
                  style={isLoading ? buttonDisabledStyle : mainButtonStyle}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span style={loaderContainerStyle}>
                      <Loader size={16} style={loaderAnimStyle} />
                      Carregando...
                    </span>
                  ) : (
                    <>
                      <UserPlus size={16} style={iconMarginStyle} />
                      Criar Conta
                    </>
                  )}
                </button>
                <button
                  onClick={() => setIsLogin(true)}
                  style={alternativeButtonInactiveStyle}
                >
                  <LogIn size={16} style={altIconMarginStyle} />
                  Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <PixPaymentModal
        visible={showPixModal}
        onClose={() => setShowPixModal(false)}
        planId={selectedPlanId}
        planPrice={15}
        onPaymentConfirmed={() => {
          onLoginSuccess?.();
          setShowPixModal(false);
          onClose();
        }}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  backdropFilter: 'blur(4px)',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.surface,
  borderRadius: 12,
  width: '95%',
  maxWidth: 500,
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${NETFLIX_COLORS.border}`,
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${NETFLIX_SPACING.xl}px ${NETFLIX_SPACING.xl * 1.5}px`,
  borderBottom: `1px solid ${NETFLIX_COLORS.border}`,
  backgroundColor: `${NETFLIX_COLORS.background}60`,
  flexShrink: 0,
};

const contentStyle: React.CSSProperties = {
  padding: `${NETFLIX_SPACING.xl * 1.5}px`,
  overflowY: 'auto',
  flex: 1,
};

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${NETFLIX_SPACING.sm}px`,
  marginBottom: `${NETFLIX_SPACING.lg}px`,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: NETFLIX_COLORS.background,
  borderRadius: 6,
  padding: `${NETFLIX_SPACING.md}px`,
  color: NETFLIX_COLORS.textPrimary,
  fontSize: 14,
  border: `1px solid ${NETFLIX_COLORS.border}`,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const paymentInfoStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${NETFLIX_SPACING.lg}px`,
  textAlign: 'center',
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: '700',
  color: NETFLIX_COLORS.textPrimary,
  margin: 0,
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: '600',
  color: NETFLIX_COLORS.textPrimary,
};

const paymentTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: '700',
  color: NETFLIX_COLORS.primary,
  margin: 0,
};

const paymentDescriptionStyle: React.CSSProperties = {
  fontSize: 14,
  color: NETFLIX_COLORS.textSecondary,
  lineHeight: 1.5,
  margin: 0,
};

const buttonStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.primary,
  padding: `${NETFLIX_SPACING.md}px ${NETFLIX_SPACING.lg}px`,
  borderRadius: 6,
  border: 'none',
  color: NETFLIX_COLORS.white,
  fontSize: 14,
  fontWeight: '700',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
};

const mainButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  flex: 1.75,
};

const buttonDisabledStyle: React.CSSProperties = {
  ...buttonStyle,
  opacity: 0.6,
  cursor: 'not-allowed',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
};

const alternativeButtonInactiveStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.background,
  border: `1px solid ${NETFLIX_COLORS.border}`,
  color: NETFLIX_COLORS.textSecondary,
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
  padding: `${NETFLIX_SPACING.sm}px ${NETFLIX_SPACING.md}px`,
  borderRadius: 6,
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
};

const iconMarginStyle: React.CSSProperties = {
  marginRight: 8,
};

const altIconMarginStyle: React.CSSProperties = {
  marginRight: 6,
};

const toggleIconMarginStyle: React.CSSProperties = {
  marginRight: 4,
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: `${NETFLIX_SPACING.md}px`,
  width: '100%',
};

const toggleButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: NETFLIX_COLORS.primary,
  fontSize: 14,
  textAlign: 'center',
  textDecoration: 'underline',
  cursor: 'pointer',
  padding: 0,
};

const dividerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: `${NETFLIX_SPACING.md}px`,
  margin: `${NETFLIX_SPACING.lg}px 0`,
};

const dividerLine: React.CSSProperties = {
  flex: 1,
  height: '1px',
  backgroundColor: NETFLIX_COLORS.border,
};

const dividerTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: NETFLIX_COLORS.textSecondary,
  textAlign: 'center',
};

const pixButtonStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.primary,
  padding: `${NETFLIX_SPACING.md}px ${NETFLIX_SPACING.xl}px`,
  borderRadius: 6,
  border: 'none',
  color: NETFLIX_COLORS.white,
  fontSize: 14,
  fontWeight: '700',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: `${NETFLIX_SPACING.md}px`,
  width: '100%',
};

const loaderContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  justifyContent: 'center',
};

const loaderAnimStyle: React.CSSProperties = {
  animation: 'spin 1s linear infinite',
};
